import type { NumericTolerances } from '../types'

export interface ComparisonSettings {
  tolerances: NumericTolerances
  highConfidenceThreshold: number
  pendingThreshold: number
  ambiguityMargin: number
  maxCandidates: number
}

type ComparisonSettingsInput = Partial<Omit<ComparisonSettings, 'tolerances'>> & {
  tolerances?: Partial<NumericTolerances>
}

export const DEFAULT_COMPARISON_SETTINGS: Readonly<ComparisonSettings> = {
  tolerances: {
    quantity: 0.000001,
    unitPrice: 0.01,
    totalPrice: 0.01,
  },
  highConfidenceThreshold: 0.88,
  pendingThreshold: 0.72,
  ambiguityMargin: 0.03,
  maxCandidates: 50,
}

function finiteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function bounded(value: unknown, fallback: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, finiteNumber(value, fallback)))
}

export function normalizeComparisonSettings(
  input: ComparisonSettingsInput = {},
): ComparisonSettings {
  const highConfidenceThreshold = bounded(
    input.highConfidenceThreshold,
    DEFAULT_COMPARISON_SETTINGS.highConfidenceThreshold,
    0.5,
    1,
  )

  return {
    tolerances: {
      quantity: Math.max(
        0,
        finiteNumber(input.tolerances?.quantity, DEFAULT_COMPARISON_SETTINGS.tolerances.quantity),
      ),
      unitPrice: Math.max(
        0,
        finiteNumber(input.tolerances?.unitPrice, DEFAULT_COMPARISON_SETTINGS.tolerances.unitPrice),
      ),
      totalPrice: Math.max(
        0,
        finiteNumber(
          input.tolerances?.totalPrice,
          DEFAULT_COMPARISON_SETTINGS.tolerances.totalPrice,
        ),
      ),
    },
    highConfidenceThreshold,
    pendingThreshold: Math.min(
      highConfidenceThreshold,
      bounded(input.pendingThreshold, DEFAULT_COMPARISON_SETTINGS.pendingThreshold, 0.3, 1),
    ),
    ambiguityMargin: bounded(
      input.ambiguityMargin,
      DEFAULT_COMPARISON_SETTINGS.ambiguityMargin,
      0,
      0.2,
    ),
    maxCandidates: Math.round(
      bounded(input.maxCandidates, DEFAULT_COMPARISON_SETTINGS.maxCandidates, 1, 50),
    ),
  }
}

export function loadComparisonSettings(): ComparisonSettings {
  try {
    const stored = localStorage.getItem('boq-diff:comparison-settings')
    if (!stored) return normalizeComparisonSettings()
    const parsed: unknown = JSON.parse(stored)
    return parsed !== null && typeof parsed === 'object'
      ? normalizeComparisonSettings(parsed)
      : normalizeComparisonSettings()
  } catch {
    return normalizeComparisonSettings()
  }
}

export function saveComparisonSettings(settings: ComparisonSettings): void {
  localStorage.setItem(
    'boq-diff:comparison-settings',
    JSON.stringify(normalizeComparisonSettings(settings)),
  )
}
